import React, { useCallback, useRef, useState } from "react";
import moment from "moment";
import toast from "react-hot-toast";
import SelectStaffMemberModal from "accountOwner/pages/AddEvent/components/SelectStaffMemberModal";
import EditOptionsModal, {
  editTypesVariables,
} from "accountOwner/pages/EditEvent/EditOptionsModal";
import { addEditEventsConst } from "accountOwner/pages/AddEvent/addEvents.constants";
import {
  setterCustomSchduler,
  setterPublishOffices,
} from "accountOwner/pages/AddEvent/addEvents.functions";
import {
  getEventListDetails,
  useCreateUpdateSchedulerEventMutation,
} from "repositories/scheduler-repository";
import { isUnassignedSchedulerEvent } from "./unassignedEvents.utils";
import {
  buildEmployeeAssignUpdateFlags,
  mapEventEmployeesForStaffModal,
} from "./assignUsersFromCard.utils";

/**
 * Opens SelectStaffMemberModal from event/shift card Assign Users, with the same
 * shift/event selection rules as Add/Edit Event, then persists via updateSchedulerEvent.
 *
 * @param {{
 *   t: Function,
 *   ownerId: number|string,
 *   canAddShift?: boolean,
 *   isStaff?: boolean,
 *   onSuccess?: () => void | Promise<void>,
 * }} options
 */
export function useAssignUsersFromCard({
  t,
  ownerId,
  canAddShift = false,
  isStaff = false,
  onSuccess,
}) {
  const createSchedulerEventMutation = useCreateUpdateSchedulerEventMutation();
  const isCommittingSelectionRef = useRef(false);

  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isAssignLoading, setIsAssignLoading] = useState(false);
  const [memberIds, setMemberIds] = useState([]);
  const [eventEmployeesSelected, setEventEmployeesSelected] = useState([]);
  const [eventDetail, setEventDetail] = useState(null);
  const [requestedDate, setRequestedDate] = useState("");
  const [pendingAssign, setPendingAssign] = useState(null);
  const [editModalConfirmation, setEditModalConfirmation] = useState(false);

  const resetAssignState = useCallback(() => {
    isCommittingSelectionRef.current = false;
    setIsAssignModalOpen(false);
    setMemberIds([]);
    setEventEmployeesSelected([]);
    setEventDetail(null);
    setRequestedDate("");
    setPendingAssign(null);
    setEditModalConfirmation(false);
  }, []);

  const persistAssignUsers = useCallback(
    async (eventData, nextMemberIds, dateKey, editType) => {
      const {
        UpdateFlags,
        changedCheck,
        eventEmployeesPayload,
      } = buildEmployeeAssignUpdateFlags(eventData, nextMemberIds);

      if (!changedCheck) {
        resetAssignState();
        return;
      }

      const hadAssignees = (eventData?.eventEmployees || []).some(
        (e) => !e?.isDeletedForThisSchedulerEvent,
      );
      const isNowUnassigned =
        !eventEmployeesPayload.length &&
        (hadAssignees || isUnassignedSchedulerEvent(eventData));

      const startTimeString = eventData?.startTime
        ? moment(eventData.startTime).format("YYYY-MM-DDTHH:mm")
        : moment(dateKey).startOf("day").format("YYYY-MM-DDTHH:mm");
      const endTimeString = eventData?.endTime
        ? moment(eventData.endTime).format("YYYY-MM-DDTHH:mm")
        : moment(dateKey).endOf("day").format("YYYY-MM-DDTHH:mm");

      const params = {
        SchedulerEventId: eventData.id,
        OwnerId: ownerId,
        Title: eventData.title || "",
        Date: moment(
          dateKey &&
            moment(dateKey)
              .startOf("day")
              .isSame(
                moment(eventData?.date || eventData?.startTime).startOf("day"),
              )
            ? eventData?.date || eventData?.startTime
            : eventData?.date || dateKey,
        ).format("YYYY-MM-DDTHH:mm"),
        StartTime: startTimeString,
        EndTime: endTimeString,
        IsAllDayEvent: eventData.isAllDayEvent || false,
        RepeatedType: eventData.repeatedType || 1,
        RepeatedDay: moment(dateKey || eventData.startTime).isoWeekday(),
        RepeatedEndDate:
          eventData.repeatedType > 1 && eventData.repeatedEndDate
            ? moment(eventData.repeatedEndDate).format("YYYY-MM-DD")
            : null,
        Location: eventData.location || "",
        BlockForPatient: eventData.blockForPatient || false,
        Note: eventData.note || "",
        PublishAsEvent: eventData.publishAsEvent || false,
        eventEmployees: eventEmployeesPayload,
        EventRoles: Array.isArray(eventData.eventRoles)
          ? eventData.eventRoles
          : [],
        EventTags: Array.isArray(eventData.eventTags) ? eventData.eventTags : [],
        CreatedAt: moment().format("YYYY-MM-DDTHH:mm"),
        ReminderBeforeInMins: eventData.reminderBeforeInMins || 0,
        isDraft: eventData.isDraft || false,
        requestedDate: moment(dateKey).format("YYYY-MM-DDTHH:mm"),
        timezoneCode: eventData.timezoneCode || moment.tz.guess(),
        EditType: editType || editTypesVariables.allEvent,
        ...(isNowUnassigned ? { IsUnassigned: true } : {}),
        UpdateFlags,
      };

      if (eventData.isShift) {
        params.Title = eventData.title || "";
        delete params.Location;
        delete params.Note;
        const shiftOfficeId =
          eventEmployeesPayload?.[0]?.officeId ?? eventData?.officeId;
        if (shiftOfficeId) {
          params.officeId = shiftOfficeId;
        }
      } else {
        params.IsAutoApprove = eventData.isAutoApprove || false;
      }

      try {
        setterCustomSchduler({
          params,
          repeatedType: eventData.repeatedType || 1,
          repeatedTypeFrequency: eventData.customRepeatPattern?.unit || 1,
          repeatedTypeEvery: eventData.customRepeatPattern?.interval || 1,
          selectionType: true,
          schedule:
            eventData.customRepeatPattern?.daysOfWeek?.map(
              (d) => d?.dayOfWeek,
            ) || [],
          selectedSlotDate:
            eventData.customRepeatPattern?.daysOfMonth?.map((d) => d?.day) ||
            [],
          selectedWeek: eventData.customRepeatPattern?.monthWeekNumber || 1,
          selectedDay:
            eventData.customRepeatPattern?.daysOfWeek?.map(
              (d) => d?.dayOfWeek,
            ) || [],
          selectedMonths:
            eventData.customRepeatPattern?.monthsOfYear?.map((m) => m?.month) ||
            [],
          isDaySwitchOn: false,
          edit: true,
          eventDetail: eventData,
        });
      } catch (error) {
        console.warn("Error in setterCustomSchduler:", error);
      }

      try {
        setterPublishOffices({
          preferredOption: {
            dropdown: eventData.publishAsEvent
              ? [{ id: "allowInvitees" }]
              : eventData.isDraft
                ? [{ id: "saveDraft" }]
                : [{ id: "inviteesOnly" }],
            otherOptions: [],
          },
          userIds: eventEmployeesPayload,
          publishAsEvent: eventData.publishAsEvent
            ? "allowInvitees"
            : eventData.isDraft
              ? "saveDraft"
              : "inviteesOnly",
          nonInviteesPublish: eventData.publishedType || "allOffices",
          params,
          officeIdForNonInvitee: Array.isArray(
            eventData.schedulerEventPublishedOffices,
          )
            ? eventData.schedulerEventPublishedOffices.filter(
                (office) => office && office.id,
              )
            : [],
          edit: true,
          isDraft: eventData.isDraft || false,
        });
      } catch (error) {
        console.warn("Error in setterPublishOffices:", error);
      }

      await createSchedulerEventMutation.mutateAsync(params);
      toast.success(t("staff.eventUpdated"));
      resetAssignState();
      await onSuccess?.();
    },
    [createSchedulerEventMutation, onSuccess, ownerId, resetAssignState, t],
  );

  const handleAssignMembersSelected = useCallback(
    async (nextMemberIds) => {
      isCommittingSelectionRef.current = true;
      setMemberIds(nextMemberIds);
      if (!eventDetail) {
        isCommittingSelectionRef.current = false;
        return;
      }

      const eventData = eventDetail;
      const dateKey = requestedDate;
      const { changedCheck } = buildEmployeeAssignUpdateFlags(
        eventData,
        nextMemberIds,
      );
      if (!changedCheck) {
        resetAssignState();
        return;
      }

      const neverCheck =
        eventData.repeatedType ===
        addEditEventsConst.repeatedOptionsValues.never;
      const isNotDrafted = !eventData.isDraft;

      if (!neverCheck && isNotDrafted) {
        setPendingAssign({
          eventData,
          memberIds: nextMemberIds,
          requestedDate: dateKey,
        });
        setEditModalConfirmation(true);
        setIsAssignModalOpen(false);
        return;
      }

      setIsAssignLoading(true);
      try {
        await persistAssignUsers(
          eventData,
          nextMemberIds,
          dateKey,
          editTypesVariables.allEvent,
        );
      } catch (error) {
        isCommittingSelectionRef.current = false;
        toast.error(error?.message || t("scheduler.invalidEventData"));
      } finally {
        setIsAssignLoading(false);
      }
    },
    [eventDetail, persistAssignUsers, requestedDate, resetAssignState, t],
  );

  const openAssignUsersModal = useCallback(
    async (eventItem, dateValue) => {
      if (!eventItem?.id) {
        toast.error(t("scheduler.invalidEventData"));
        return;
      }

      const dateKey = moment(
        dateValue || eventItem.requestedDate || eventItem.startTime,
      ).format("YYYY-MM-DD");

      setIsAssignLoading(true);
      try {
        const eventDetails = await getEventListDetails(eventItem.id, dateKey);
        const eventData = eventDetails?.data;
        if (!eventData) {
          toast.error(t("scheduler.eventNotFound"));
          return;
        }

        const mappedEmployees = mapEventEmployeesForStaffModal(
          eventData.eventEmployees,
        );
        setEventDetail(eventData);
        setRequestedDate(dateKey);
        setMemberIds(mappedEmployees);
        setEventEmployeesSelected(mappedEmployees);
        setIsAssignModalOpen(true);
      } catch (error) {
        toast.error(error?.message || t("scheduler.eventNotFound"));
      } finally {
        setIsAssignLoading(false);
      }
    },
    [t],
  );

  const renderAssignUsersModals = useCallback(() => {
    const isShift = Boolean(
      eventDetail?.isShift ?? pendingAssign?.eventData?.isShift,
    );

    return (
      <>
        {isAssignModalOpen && eventDetail && (
          <SelectStaffMemberModal
            isOpen={isAssignModalOpen}
            closeStepOneModal={(open) => {
              if (open) {
                setIsAssignModalOpen(true);
                return;
              }
              setIsAssignModalOpen(false);
              // Select commits via setMemberIds; avoid wiping pending edit confirm.
              if (isCommittingSelectionRef.current) {
                isCommittingSelectionRef.current = false;
                return;
              }
              resetAssignState();
            }}
            ownerId={ownerId}
            setMemberIds={handleAssignMembersSelected}
            memberIds={memberIds}
            forEdit={true}
            eventOrShiftTab={Boolean(eventDetail?.isShift)}
            canAddShift={Boolean(
              (isStaff || canAddShift) && eventDetail?.isShift,
            )}
            IsOwnerInclude={true}
            eventEmployeesSelected={eventEmployeesSelected}
          />
        )}
        {editModalConfirmation && pendingAssign && (
          <EditOptionsModal
            isOpen={editModalConfirmation}
            closeStepOneModal={(open) => {
              if (!open) {
                resetAssignState();
              } else {
                setEditModalConfirmation(open);
              }
            }}
            loader={isAssignLoading}
            eventKey={{
              eventOrShift: t(
                `accountOwner.${isShift ? "shift" : "event"}Tab`,
              ).toLowerCase(),
            }}
            eventOrShiftTab={isShift}
            busySlots={false}
            repeatedOptionChanged={false}
            handleConfirm={async (value) => {
              setIsAssignLoading(true);
              try {
                await persistAssignUsers(
                  pendingAssign.eventData,
                  pendingAssign.memberIds,
                  pendingAssign.requestedDate,
                  value,
                );
              } catch (error) {
                toast.error(error?.message || t("scheduler.invalidEventData"));
              } finally {
                setIsAssignLoading(false);
              }
            }}
          />
        )}
      </>
    );
  }, [
    canAddShift,
    editModalConfirmation,
    eventDetail,
    eventEmployeesSelected,
    handleAssignMembersSelected,
    isAssignLoading,
    isAssignModalOpen,
    isStaff,
    memberIds,
    ownerId,
    pendingAssign,
    persistAssignUsers,
    resetAssignState,
    t,
  ]);

  return {
    isAssignLoading,
    openAssignUsersModal,
    renderAssignUsersModals,
  };
}
